<template>
    <div class="ds-dossier-body">
        <div class="ds-dossier-subhead">
            <button type="button" class="ds-dossier-back" :disabled="pending" @click="emit('back')"><BackIcon />Личные данные</button>
        </div>
        <span class="ds-dossier-subtitle">Трудоустройство</span>
        <!-- Подразделение существующего ТУ — подпись, не поле переноса. -->
        <div class="ds-dossier-caption">
            <span class="ds-dossier-section-title">{{ employment.departmentName || DASH }}</span>
            <span class="ds-dossier-muted">{{ fullName(person) || DASH }}</span>
        </div>

        <!-- Employment.public_api_fields: замок только у полей массива этого ТУ (не наследуется от User). -->
        <div v-if="locksKnown && oneCLockedCount" class="ds-dossier-info">
            <LockIcon />
            <span>Поля с замком приходят из 1С и меняются там.</span>
        </div>
        <div v-if="!locksKnown" class="ds-dossier-info is-error" role="alert">
            <LockIcon />
            <span>Не удалось проверить поля из 1С — изменение трудоустройства недоступно. <button type="button" class="ds-dossier-link" :disabled="pending" @click="reloadUser()">Обновить</button></span>
        </div>

        <div class="ds-dossier-grid">
            <div class="ds-dossier-field">
                <span class="ds-dossier-field-label">Должность</span>
                <span v-if="locked('position')" class="ds-dossier-locked" :title="lockTitle"><span>{{ draft.position?.name || DASH }}</span><LockIcon /></span>
                <dropdown v-else v-model="draft.position" :options="scheduleStore.positions" :disabled="pending" search>
                    <span class="truncate">{{ draft.position?.name || 'Выберите' }}</span>
                </dropdown>
                <span v-if="errorOf('position')" class="ds-dossier-field-error">{{ errorOf('position') }}</span>
            </div>
            <div class="ds-dossier-field">
                <span class="ds-dossier-field-label">Дата начала</span>
                <span v-if="locked('hiredDate')" class="ds-dossier-locked" :title="lockTitle"><span>{{ draft.hiredDate ? draft.hiredDate.format('DD.MM.YYYY') : DASH }}</span><LockIcon /></span>
                <div v-else class="ds-glass-date"><AppDateInput v-model="hiredDateModel" /></div>
                <span v-if="errorOf('hiredDate')" class="ds-dossier-field-error">{{ errorOf('hiredDate') }}</span>
            </div>
            <div class="ds-dossier-field">
                <span class="ds-dossier-field-label">Доля рабочих часов, %</span>
                <span v-if="locked('percentage')" class="ds-dossier-locked" :title="lockTitle"><span>{{ draft.percentage || DASH }}</span><LockIcon /></span>
                <base-input v-else :model-value="draft.percentage" :label="false" max-length="3" :disabled="pending" @update:model-value="draft.percentage = String($event).replace(/\D/g, '')" />
                <span v-if="errorOf('percentage')" class="ds-dossier-field-error">{{ errorOf('percentage') }}</span>
            </div>
            <div class="ds-dossier-field">
                <span class="ds-dossier-field-label">Доступность</span>
                <span v-if="locked('shiftSchedule')" class="ds-dossier-locked" :title="lockTitle"><span>{{ draft.shiftSchedule?.name || DASH }}</span><LockIcon /></span>
                <dropdown v-else v-model="draft.shiftSchedule" :options="scheduleStore.shiftSchedules" :disabled="pending">
                    <span class="truncate">{{ draft.shiftSchedule?.name || 'Выберите' }}</span>
                </dropdown>
                <span v-if="errorOf('shiftSchedule')" class="ds-dossier-field-error">{{ errorOf('shiftSchedule') }}</span>
            </div>
            <div class="ds-dossier-field">
                <span class="ds-dossier-field-label">Ставка в час</span>
                <span v-if="locked('salaryHour')" class="ds-dossier-locked" :title="lockTitle"><span>{{ draft.salaryHour || DASH }}</span><LockIcon /></span>
                <base-input v-else :model-value="draft.salaryHour" :label="false" max-length="7" :disabled="pending" @update:model-value="draft.salaryHour = String($event).replace(/\D/g, '')" />
                <span v-if="errorOf('salaryHour')" class="ds-dossier-field-error">{{ errorOf('salaryHour') }}</span>
            </div>
            <div class="ds-dossier-field">
                <span class="ds-dossier-field-label">Табельный код</span>
                <!-- Непустой табельный код readonly независимо от массива; пустой можно заполнить, если не закреплён. -->
                <span v-if="tabelLocked" class="ds-dossier-locked" :title="locked('tabelCode') ? lockTitle : 'Табельный код уже задан'"><span>{{ draft.tabelCode || DASH }}</span><LockIcon /></span>
                <base-input v-else v-model="draft.tabelCode" :label="false" max-length="20" :disabled="pending" />
                <span v-if="errorOf('tabelCode')" class="ds-dossier-field-error">{{ errorOf('tabelCode') }}</span>
            </div>
        </div>

        <div class="ds-dossier-section-head"><span class="ds-dossier-section-title">Завершение</span></div>
        <div class="ds-dossier-ending">
            <span class="ds-dossier-muted">{{ endingText }}</span>
            <button v-if="canFire" type="button" class="ds-dossier-danger-link" :disabled="pending" @click="isFireOpen = true">Уволить…</button>
        </div>
        <!-- fired_at из 1С запрещает увольнение с фронта. -->
        <span v-if="!employment.firedAt && firedAtLocked" class="ds-dossier-note">{{ locksKnown ? 'Дата увольнения заполняется из 1С' : LOCKS_UNKNOWN_TITLE }}</span>
    </div>

    <div class="ds-dossier-footer">
        <span v-if="message" class="ds-dossier-footer-message" :class="{ 'ds-dossier-field-error': !written }" role="alert">
            {{ message }}
            <button v-if="written" type="button" class="ds-dossier-link" @click="retryRead">Обновить</button>
        </span>
        <button type="button" class="ds-glass-btn ds-glass-btn-secondary" :disabled="pending" @click="emit('back')">Отмена</button>
        <button type="button" class="ds-glass-btn ds-glass-btn-primary" :disabled="!canSave" @click="save">{{ pending ? 'Сохранение…' : 'Сохранить' }}</button>
    </div>

    <DossierFireDialog
        v-if="isFireOpen"
        :person
        :employment
        :today="today"
        @close="isFireOpen = false"
        @fired="onFired"
    />
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import type { Dayjs } from 'dayjs'
import AppDateInput from '@/ui/AppDateInput.vue'
import { editEmployment } from '@/api/users'
import { useScheduleStore } from '@/stores/scheduleStore'
import {
    type DossierEmployment,
    type DossierPerson,
    type EmploymentDraft,
    DASH,
    LOCKS_UNKNOWN_TITLE,
    ONE_C_LOCK_TITLE,
    buildEmploymentPatch,
    describeWriteError,
    employmentDraftFrom,
    fieldErrorsOf,
    firedDayKey,
    formatDate,
    fullName,
    isApiLocked,
    omitLocked,
    validateEmploymentDraft,
} from './dossierModel'
import { BackIcon, LockIcon } from './dossierIcons'
import DossierFireDialog from './DossierFireDialog.vue'

// Подэкран одного ТУ (вариант B r2): «Сохранить» — changed-only PATCH только этого Employment, без User.
// Открывается лишь при can_edit_employment и подразделении ТУ в myDepartments (проверяет список).
const props = defineProps<{
    person: DossierPerson
    employment: DossierEmployment
    today: Dayjs
    afterWrite: () => Promise<boolean>
    // Перечитывание User без записи (актуализация public_api_fields после серверного отказа).
    reloadUser: () => Promise<boolean>
}>()
// done — запись подтверждена и данные перечитаны (или перечитывание отдельно повторено).
const emit = defineEmits<{ back: [], done: [] }>()

const scheduleStore = useScheduleStore()

const base = ref<DossierEmployment>({ ...props.employment })
const draft = reactive<EmploymentDraft>(employmentDraftFrom(props.employment, {
    positions: scheduleStore.positions,
    shiftSchedules: scheduleStore.shiftSchedules,
}))
const hiredDateModel = computed({
    get: () => draft.hiredDate,
    set: (value: unknown) => { draft.hiredDate = (value as Dayjs | null) ?? null },
})
// Имена полей Employment в public_api_fields и в 400 body.
const EMPLOYMENT_API_FIELDS: Record<keyof EmploymentDraft, string> = {
    position: 'position',
    hiredDate: 'hired_at',
    percentage: 'percentage',
    shiftSchedule: 'shift_schedule',
    salaryHour: 'salary_hour',
    tabelCode: 'employee_tabel_code',
}
// Актуальные locks ТУ из перечитанного User; null — массив не получен, запись ТУ недоступна.
const locks = computed(() => props.employment.publicApiFields)
const locksKnown = computed(() => locks.value !== null)
const locked = (key: keyof EmploymentDraft) => isApiLocked(locks.value, EMPLOYMENT_API_FIELDS[key])
const lockTitle = computed(() => (locksKnown.value ? ONE_C_LOCK_TITLE : LOCKS_UNKNOWN_TITLE))
const oneCLockedCount = computed(() => (Object.keys(EMPLOYMENT_API_FIELDS) as (keyof EmploymentDraft)[]).filter(locked).length)
const tabelLocked = computed(() => locked('tabelCode') || Boolean(base.value.tabelCode))
const firedAtLocked = computed(() => isApiLocked(locks.value, 'fired_at'))
// «Уволить…»: пустой fired_at и fired_at не закреплён интеграцией (подэкран открыт только при праве).
const canFire = computed(() => !props.employment.firedAt && !firedAtLocked.value)

const patch = computed(() => omitLocked(buildEmploymentPatch(base.value, draft), locks.value))
const touched = ref(false)
watch(draft, () => { touched.value = true }, { deep: true })
// Закреплённые поля не валидируются: значение из 1С не блокирует сохранение остальных.
const errors = computed(() => {
    const result = validateEmploymentDraft(draft, base.value)
    for (const key of Object.keys(result) as (keyof EmploymentDraft)[]) {
        if (locked(key)) delete result[key]
    }
    return result
})
const valid = computed(() => Object.keys(errors.value).length === 0)
// Серверные 400 по полям; сбрасываются при правке draft.
const serverErrors = ref<Partial<Record<keyof EmploymentDraft, string>>>({})
watch(draft, () => { serverErrors.value = {} }, { deep: true })
const errorOf = (field: keyof EmploymentDraft) => serverErrors.value[field] ?? (touched.value ? errors.value[field] : undefined)

const pending = ref(false)
const written = ref(false)
const message = ref('')
const canSave = computed(() => !pending.value && !written.value && Object.keys(patch.value).length > 0 && valid.value)

const endingText = computed(() => {
    const fired = firedDayKey(props.employment)
    if (!fired) return props.employment.hiredAt ? `Работает с ${formatDate(props.employment.hiredAt)}` : 'Работает'
    return fired >= props.today.format('YYYY-MM-DD')
        ? `Последний рабочий день ${formatDate(props.employment.firedAt)}`
        : `Уволен с ${formatDate(props.employment.firedAt)}`
})

async function save() {
    if (!canSave.value) return
    pending.value = true
    message.value = ''
    try {
        await editEmployment(base.value.id, { ...patch.value }, { suppressGlobalErrorToast: true })
    } catch (error) {
        // Ошибка write: draft и экран остаются; автоповтора нет. 400 — у поля и перечитывание locks.
        message.value = describeWriteError(error, 'Трудоустройство не сохранено')
        const byField = fieldErrorsOf(error)
        const mapped: Partial<Record<keyof EmploymentDraft, string>> = {}
        for (const key of Object.keys(EMPLOYMENT_API_FIELDS) as (keyof EmploymentDraft)[]) {
            if (byField[EMPLOYMENT_API_FIELDS[key]]) mapped[key] = byField[EMPLOYMENT_API_FIELDS[key]]
        }
        if (Object.keys(byField).length) await props.reloadUser()
        serverErrors.value = mapped
        pending.value = false
        return
    }
    written.value = true
    await finishRead()
}

// Успешный write с ошибкой GET не объявляется несохранённым: повторяется только чтение.
async function finishRead() {
    pending.value = true
    const refreshed = await props.afterWrite()
    pending.value = false
    if (refreshed) {
        emit('done')
        return
    }
    message.value = 'Сохранено, но обновить данные не удалось.'
}

const retryRead = () => { void finishRead() }

// ---------- Увольнение ----------
const isFireOpen = ref(false)
async function onFired() {
    isFireOpen.value = false
    written.value = true
    await finishRead()
}
</script>
